import { useTranslation } from '@jq4race/i18n';
import type { EventTemplateResponse } from '@jq4race/shared';
import {
  Badge,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@jq4race/ui';
import { LayoutTemplate } from 'lucide-react';

interface EventTemplatesTableProps {
  templates: EventTemplateResponse[];
  loading: boolean;
}

/** `defaultConfig` is a free-form JSON blob (R1.7) — chargeMode/photoPolicy
 * are optional hints set by CreateEventTemplateForm, not schema-enforced.
 * The raw enum value (e.g. "per_attendee") is intentionally left untranslated
 * here — it is not one of the standard photo-policy values below and has no
 * catalog entry; only spaces are normalized for readability. */
function defaultChargeModeLabel(defaultConfig: Record<string, unknown>): string {
  const value = defaultConfig.chargeMode;
  return typeof value === 'string' ? value.replaceAll('_', ' ') : '—';
}

function usePhotoPolicyLabel() {
  const { t } = useTranslation('events');
  return (defaultConfig: Record<string, unknown>): string => {
    const value = defaultConfig.photoPolicy;
    if (value === 'required_4' || value === 'deferred' || value === 'optional') {
      return t(`constants.photoPolicy.${value}`);
    }
    return '—';
  };
}

export function EventTemplatesTable({ templates, loading }: EventTemplatesTableProps) {
  const { t } = useTranslation('events');
  const defaultPhotoPolicyLabel = usePhotoPolicyLabel();
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('eventTemplatesTable.name')}</TableHead>
          <TableHead>{t('eventTemplatesTable.category')}</TableHead>
          <TableHead>{t('eventTemplatesTable.defaultChargeMode')}</TableHead>
          <TableHead>{t('eventTemplatesTable.defaultPhotoPolicy')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading && templates.length === 0 ? (
          <SkeletonRows />
        ) : templates.length === 0 ? (
          <TableEmpty colSpan={4}>
            <EmptyState />
          </TableEmpty>
        ) : (
          templates.map((template) => (
            <TableRow key={template.id}>
              <TableCell>
                <span className="font-medium text-jq-charcoal">{template.name}</span>
              </TableCell>
              <TableCell>
                <Badge variant="secondary" className="capitalize">
                  {t(`constants.templateCategory.${template.category}`)}
                </Badge>
              </TableCell>
              <TableCell className="text-jq-graphite capitalize">
                {defaultChargeModeLabel(template.defaultConfig)}
              </TableCell>
              <TableCell className="text-jq-graphite">
                {defaultPhotoPolicyLabel(template.defaultConfig)}
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positional
        <TableRow key={`skeleton-${i}`}>
          {Array.from({ length: 4 }).map((_, j) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positional
            <TableCell key={`cell-${j}`}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function EmptyState() {
  const { t } = useTranslation('events');
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <LayoutTemplate className="h-10 w-10 text-jq-stone" strokeWidth={1.25} />
      <p className="text-[14px] font-medium text-jq-charcoal">
        {t('eventTemplatesTable.empty.title')}
      </p>
      <p className="max-w-sm text-[13px] text-jq-graphite">
        {t('eventTemplatesTable.empty.description')}
      </p>
    </div>
  );
}
